import type { ParseNode } from './ParseTree';

/**
 * Evaluate a parse tree with provided variables and functions
 *
 * This evaluator is optimized for maximum performance:
 * - Minimal object/array allocations
 * - Direct tree walking
 * - Inline operations
 * - No intermediate data structures
 *
 * Undefined propagation:
 * - Any operation with undefined returns undefined
 * - Missing parameters return undefined
 * - Missing functions return undefined
 * - No errors are thrown - undefined indicates evaluation failure
 *
 * @param node - The parse tree node to evaluate
 * @param parameters - Map of parameter names to their values (number | undefined)
 * @param functions - Map of function names to JavaScript functions
 * @returns The numeric result, or undefined if evaluation failed
 */
export function evaluate(
  node: ParseNode,
  parameters: Record<string, number | undefined>,
  functions: Record<string, (...args: (number | undefined)[]) => number | undefined>,
): number | undefined {
  // Handle primitive nodes
  if (typeof node === 'number') {
    return node;
  }

  if (typeof node === 'string') {
    return parameters[node];
  }

  // Handle operation nodes
  const type = node[0];

  switch (type) {
    case 'add': {
      const left = evaluate(node[1] as ParseNode, parameters, functions);
      if (left === undefined) return undefined;
      const right = evaluate(node[2] as ParseNode, parameters, functions);
      if (right === undefined) return undefined;
      return left + right;
    }

    case 'subtract': {
      const left = evaluate(node[1] as ParseNode, parameters, functions);
      if (left === undefined) return undefined;
      const right = evaluate(node[2] as ParseNode, parameters, functions);
      if (right === undefined) return undefined;
      return left - right;
    }

    case 'multiply': {
      const left = evaluate(node[1] as ParseNode, parameters, functions);
      if (left === undefined) return undefined;
      const right = evaluate(node[2] as ParseNode, parameters, functions);
      if (right === undefined) return undefined;
      return left * right;
    }

    case 'divide': {
      const left = evaluate(node[1] as ParseNode, parameters, functions);
      if (left === undefined) return undefined;
      const right = evaluate(node[2] as ParseNode, parameters, functions);
      if (right === undefined) return undefined;
      return left / right;
    }

    case 'power': {
      const base = evaluate(node[1] as ParseNode, parameters, functions);
      if (base === undefined) return undefined;
      const exponent = evaluate(node[2] as ParseNode, parameters, functions);
      if (exponent === undefined) return undefined;
      return base ** exponent;
    }

    case 'negate': {
      const value = evaluate(node[1] as ParseNode, parameters, functions);
      if (value === undefined) return undefined;
      return -value;
    }

    case 'positive': {
      const value = evaluate(node[1] as ParseNode, parameters, functions);
      return value; // No-op: value is already a number
    }

    case 'call': {
      const funcName = node[1] as string;
      const func = functions[funcName];
      if (func === undefined) return undefined;

      // Optimize for common argument counts to avoid array allocation
      const argCount = node.length - 2;

      if (argCount === 0) {
        return func();
      }

      if (argCount === 1) {
        const arg0 = evaluate(node[2] as ParseNode, parameters, functions);
        return func(arg0);
      }

      if (argCount === 2) {
        const arg0 = evaluate(node[2] as ParseNode, parameters, functions);
        const arg1 = evaluate(node[3] as ParseNode, parameters, functions);
        return func(arg0, arg1);
      }

      if (argCount === 3) {
        const arg0 = evaluate(node[2] as ParseNode, parameters, functions);
        const arg1 = evaluate(node[3] as ParseNode, parameters, functions);
        const arg2 = evaluate(node[4] as ParseNode, parameters, functions);
        return func(arg0, arg1, arg2);
      }

      // For 4+ arguments, we must allocate an array
      // Pre-allocate with exact size for efficiency
      const args: (number | undefined)[] = new Array(argCount);
      for (let i = 0; i < argCount; i++) {
        args[i] = evaluate(node[i + 2] as ParseNode, parameters, functions);
      }
      return func(...args);
    }

    // Comparison operators - return 1 for true, 0 for false
    case 'gt': {
      const left = evaluate(node[1] as ParseNode, parameters, functions);
      if (left === undefined) return undefined;
      const right = evaluate(node[2] as ParseNode, parameters, functions);
      if (right === undefined) return undefined;
      return left > right ? 1 : 0;
    }

    case 'lt': {
      const left = evaluate(node[1] as ParseNode, parameters, functions);
      if (left === undefined) return undefined;
      const right = evaluate(node[2] as ParseNode, parameters, functions);
      if (right === undefined) return undefined;
      return left < right ? 1 : 0;
    }

    case 'gte': {
      const left = evaluate(node[1] as ParseNode, parameters, functions);
      if (left === undefined) return undefined;
      const right = evaluate(node[2] as ParseNode, parameters, functions);
      if (right === undefined) return undefined;
      return left >= right ? 1 : 0;
    }

    case 'lte': {
      const left = evaluate(node[1] as ParseNode, parameters, functions);
      if (left === undefined) return undefined;
      const right = evaluate(node[2] as ParseNode, parameters, functions);
      if (right === undefined) return undefined;
      return left <= right ? 1 : 0;
    }

    case 'eq': {
      const left = evaluate(node[1] as ParseNode, parameters, functions);
      if (left === undefined) return undefined;
      const right = evaluate(node[2] as ParseNode, parameters, functions);
      if (right === undefined) return undefined;
      return left === right ? 1 : 0;
    }

    case 'neq': {
      const left = evaluate(node[1] as ParseNode, parameters, functions);
      if (left === undefined) return undefined;
      const right = evaluate(node[2] as ParseNode, parameters, functions);
      if (right === undefined) return undefined;
      return left !== right ? 1 : 0;
    }

    default:
      // Unknown node type - return undefined
      return undefined;
  }
}
